import {
    IListenerJourney,
    IListenerJourneyContract,
    IListenerJourneyFinalExamination,
    IListenerJourneyPrinting,
} from '@api/listeners/state.types'
import {
    ListenerJourneyGraph,
    ListenerJourneyGraphEdge,
    ListenerJourneyGraphNode,
    ListenerJourneyNodeKind,
    ListenerJourneyNodeState,
    ListenerJourneyRecord,
} from './types'

export const buildListenerJourneyRegistry = (data: IListenerJourney): ListenerJourneyRecord[] => {
    const fullNameFrom = data.listener.fullNameFrom || getFullName(data)
    const missingStageStatus = (type: string) => !type?.trim() ? 'Нет данных'
        : /^(не требуется|без аттестации|без документа)$/i.test(type.trim()) ? 'Не требуется' : 'Не создано'
    const applicationIDs = [...new Set([
        ...data.applications.map(item => item.applicationID),
        ...data.contracts.map(item => item.applicationID),
        ...data.commercialOffers.map(item => item.applicationID),
    ])]
    return applicationIDs.map(applicationID => {
        const application = data.applications.find(item => item.applicationID === applicationID)
        const contracts = data.contracts.filter(item => item.applicationID === applicationID)
        const uniqueContracts = [...new Map(contracts.map(item => [item.contractID, item])).values()]
        const offers: ListenerJourneyRecord[] = data.commercialOffers.filter(item => item.applicationID === applicationID).map(item => ({
            id: `offer-${item.commercialOfferID}`, title: `КП №${item.commercialOfferID}`, status: item.statusName || 'Нет данных',
            summary: item.commercialOfferDate, action: { type: 'commercialOffer', cardID: item.commercialOfferID },
            fields: [['Дата предложения', item.commercialOfferDate], ['Дата создания', item.dateCreate]],
        }))
        const contractRows: ListenerJourneyRecord[] = uniqueContracts.map(contract => {
            const signature = data.signatures.find(item => item.contractID === contract.contractID)
            const company = data.companies.find(item => item.companyID === contract.companyID)
            const programs = [...new Map(contracts.filter(item => item.contractID === contract.contractID && item.contractProgramID > 0).map(item => [item.contractProgramID, item])).values()]
            const children: ListenerJourneyRecord[] = []
            if (contract.companyID) children.push({
                id: `company-${contract.contractID}-${contract.companyID}`, title: company?.companyName || contract.companyName || 'Контрагент', status: 'Связанная организация',
                summary: `ИНН ${company?.companyINN || contract.companyINN || 'не указан'}`,
                action: { type: 'company', cardID: contract.companyID },
                fields: [['Наименование', company?.companyName || contract.companyName], ['ИНН', company?.companyINN || contract.companyINN]],
            })
            children.push({
                id: `signature-${contract.contractID}`, title: 'Подписание', status: signature?.statusName || (signature ? 'Нет данных' : 'Не создано'),
                summary: signature?.dateCreate, state: signature ? 'active' : 'pending',
                action: signature ? { type: 'signature', cardID: contract.contractID, contractNumber: contract.contractNumber } : undefined,
                fields: [['Договор', contract.contractNumber], ['Дата создания', signature?.dateCreate || '']],
            })
            programs.forEach(program => {
                const finals = data.finalExaminations.filter(item => item.contractID === contract.contractID && item.contractProgramID === program.contractProgramID)
                const printings = data.printings.filter(item => item.contractID === contract.contractID && item.contractProgramID === program.contractProgramID)
                const programID = `${contract.contractID}-${program.contractProgramID}`
                const stages: ListenerJourneyRecord[] = finals.map(item => ({
                    id: `final-${item.finalExaminationID}`, title: `Итоговая аттестация №${item.finalExaminationID}`,
                    status: item.toPrint ? 'Допущен к печати' : item.isArchive ? 'В архиве' : 'На проверке',
                    state: item.toPrint || item.isArchive ? 'complete' : 'active', summary: item.attestationTypeName,
                    action: { type: 'finalExamination', cardID: item.finalExaminationID, fullNameFrom, attestationType: item.attestationTypeName || program.attestationTypeName, dataBase: program.dataBase },
                    fields: [['Вид аттестации', item.attestationTypeName], ['Тема работы', item.themeIAR], ['Оценка работы', item.iarScore], ['Оценка теста', item.testScore], ['Дата создания', item.dateCreate]],
                }))
                if (!finals.length) stages.push({ id: `final-missing-${programID}`, title: 'Итоговая аттестация', status: missingStageStatus(program.attestationTypeName), state: 'pending', fields: [['Вид аттестации', program.attestationTypeName]] })
                printings.forEach(item => {
                    stages.push({
                        id: `print-${item.printID}`, title: `Печать №${item.printID}`, status: item.printStatusName || 'Нет данных',
                        state: item.datePrint ? 'complete' : 'active', summary: [program.documentTypeName, item.diplomNumber].filter(Boolean).join(' №'),
                        action: { type: 'printing', cardID: item.printID, fullNameFrom, dataBase: item.dataBase || program.dataBase },
                        fields: [['Вид документа', program.documentTypeName], ['Номер', item.diplomNumber], ['Регистрационный номер', item.diplomRegNumber], ['Дата выдачи', item.diplomDateIssue], ['Дата печати', item.datePrint]],
                    }, {
                        id: `scan-${item.printID}`, title: 'Скан документа', status: item.scan ? 'Загружен' : 'Не загружен',
                        state: item.scan ? 'complete' : 'pending', summary: item.scan?.fileName,
                        action: item.scan ? { type: 'scan', fileUrl: item.scan.filePath, fileName: item.scan.fileName } : undefined,
                        fields: [['Имя файла', item.scan?.fileName || ''], ['Размер', item.scan?.fileSize || ''], ['Дата загрузки', item.scan?.dateCreate || '']],
                    })
                })
                if (!printings.length) stages.push({ id: `print-missing-${programID}`, title: 'Печать документа', status: missingStageStatus(program.documentTypeName), state: 'pending', fields: [['Вид документа', program.documentTypeName]] })
                children.push({
                    id: `program-${programID}`, title: program.courseName || `Программа №${program.contractProgramID}`,
                    status: 'Программа обучения', summary: [program.dateEducationStart, program.dateEducationEnd].filter(Boolean).join(' — '),
                    fields: [['Начало обучения', program.dateEducationStart], ['Окончание обучения', program.dateEducationEnd], ['Документ', program.documentTypeName]], children: stages,
                })
            })
            children.push({
                id: `delivery-${contract.contractID}`, title: 'Доставка', status: contract.isDelivery ? 'Доставлено' : 'Не завершена',
                state: contract.isDelivery ? 'complete' : 'pending', summary: contract.deliveryAddress,
                action: contract.contractStatusID === 4 || contract.contractStatusID === 5 || contract.isScan || contract.isDelivery ? { type: 'delivery', cardID: contract.contractID } : undefined,
                fields: [['Адрес доставки', contract.deliveryAddress], ['Договор', contract.contractNumber]],
            })
            return {
                id: `contract-${contract.contractID}`, title: contract.contractNumber ? `Договор ${contract.contractNumber}` : `Договор №${contract.contractID}`,
                status: contract.contractStatusName || 'Нет данных', summary: `${contract.contractDate || ''} · Программ: ${programs.length}`,
                state: getContractState(contract), action: { type: 'contract', cardID: contract.contractID },
                fields: [['Номер договора', contract.contractNumber], ['Дата договора', contract.contractDate], ['Тип договора', contract.contractTypeName]], children,
            }
        })
        return {
            id: `application-${applicationID}`, title: applicationID > 0 ? `Заявка №${applicationID}` : 'Без связанной заявки',
            status: application?.statusName || 'Нет данных', summary: `${application?.dateCreate || ''} · Договоров: ${uniqueContracts.length}`,
            action: applicationID > 0 ? { type: 'application', cardID: applicationID } : undefined,
            fields: [['Дата создания', application?.dateCreate || ''], ['Источник', application?.dataBase === 'yaskill' ? 'YaSkill' : application?.dataBase === 'modern_academy' ? 'ModSkill' : application?.dataBase || '']],
            children: [...offers, ...contractRows],
        }
    })
}

const NODE_WIDTH = 250
const NODE_HEIGHT = 116
const LISTENER_WIDTH = 286
const LISTENER_HEIGHT = 138
const MIN_GRAPH_WIDTH = 1180
const MIN_GRAPH_HEIGHT = 980
const GRAPH_PADDING_X = 120
const GRAPH_PADDING_Y = 96
const HORIZONTAL_GAP = 56
const VERTICAL_GAP = 72

const STAGE_LEVELS: Record<ListenerJourneyNodeKind, number> = {
    listener: 0,
    application: 1,
    commercialOffer: 2,
    contract: 3,
    company: 4,
    signature: 4,
    finalExamination: 5,
    printing: 6,
    scan: 7,
    delivery: 8,
}

const asText = (value: unknown) => String(value || '').trim()
const getFullName = (data: IListenerJourney) => [
    data.listener.lastName,
    data.listener.firstName,
    data.listener.secondName,
].filter(Boolean).join(' ')

const branchKey = (contractID: number, contractProgramID: number) => `${contractID}-${contractProgramID}`

const getContractState = (contract: IListenerJourneyContract): ListenerJourneyNodeState => {
    if (contract.contractStatusID === 6) return 'attention'
    if (contract.isDelivery || contract.contractStatusID === 5) return 'complete'
    return 'active'
}

const getFinalExaminationState = (item: IListenerJourneyFinalExamination): ListenerJourneyNodeState =>
    item.toPrint || item.isArchive ? 'complete' : 'active'

const getPrintingState = (item: IListenerJourneyPrinting): ListenerJourneyNodeState =>
    asText(item.diplomNumber) || asText(item.datePrint) ? 'complete' : 'active'

const getStageTop = (kind: ListenerJourneyNodeKind) => {
    const level = STAGE_LEVELS[kind]
    if (level === 0) return GRAPH_PADDING_Y

    return GRAPH_PADDING_Y + LISTENER_HEIGHT + VERTICAL_GAP + (level - 1) * (NODE_HEIGHT + VERTICAL_GAP)
}

const positionNodes = (nodes: ListenerJourneyGraphNode[], edges: ListenerJourneyGraphEdge[]): ListenerJourneyGraph => {
    const nodeByID = new Map(nodes.map((node) => [node.id, node]))
    const rootNode = nodes.find((node) => node.kind === 'listener')
    if (!rootNode) return {nodes, edges, width: MIN_GRAPH_WIDTH, height: MIN_GRAPH_HEIGHT, center: {x: 0, y: 0}}

    const incomingEdges = new Map<string, ListenerJourneyGraphEdge[]>()
    edges.forEach((edge) => incomingEdges.set(edge.to, [...(incomingEdges.get(edge.to) || []), edge]))

    const parentByID = new Map<string, string>()
    nodes.forEach((node) => {
        if (node.id === rootNode.id) return
        const candidates = incomingEdges.get(node.id) || []
        const parentEdge = candidates.find((edge) => edge.kind === 'process') || candidates[0]
        const parentID = parentEdge && nodeByID.has(parentEdge.from) && parentEdge.from !== node.id
            ? parentEdge.from
            : rootNode.id
        parentByID.set(node.id, parentID)
    })

    const childrenByID = new Map(nodes.map((node) => [node.id, [] as string[]]))
    nodes.forEach((node) => {
        const parentID = parentByID.get(node.id)
        if (parentID) childrenByID.get(parentID)?.push(node.id)
    })

    const spanByID = new Map<string, number>()
    const measureSubtree = (nodeID: string, ancestors = new Set<string>()): number => {
        const cachedSpan = spanByID.get(nodeID)
        if (cachedSpan !== undefined) return cachedSpan
        const node = nodeByID.get(nodeID)
        if (!node || ancestors.has(nodeID)) return NODE_WIDTH

        const nextAncestors = new Set(ancestors).add(nodeID)
        const children = childrenByID.get(nodeID) || []
        const childrenSpan = children.reduce((total, childID, index) => (
            total + measureSubtree(childID, nextAncestors) + (index > 0 ? HORIZONTAL_GAP : 0)
        ), 0)
        const span = Math.max(node.width, childrenSpan)
        spanByID.set(nodeID, span)
        return span
    }

    const rootSpan = measureSubtree(rootNode.id)
    const naturalWidth = rootSpan + GRAPH_PADDING_X * 2
    const width = Math.max(MIN_GRAPH_WIDTH, naturalWidth)
    const contentLeft = GRAPH_PADDING_X + (width - naturalWidth) / 2
    const positionedByID = new Map<string, ListenerJourneyGraphNode>()

    const placeSubtree = (nodeID: string, left: number) => {
        const node = nodeByID.get(nodeID)
        if (!node) return

        const span = spanByID.get(nodeID) || node.width
        const centerX = left + span / 2
        positionedByID.set(nodeID, {
            ...node,
            x: centerX - node.width / 2,
            y: getStageTop(node.kind),
        })

        const children = childrenByID.get(nodeID) || []
        const childrenSpan = children.reduce((total, childID, index) => (
            total + (spanByID.get(childID) || NODE_WIDTH) + (index > 0 ? HORIZONTAL_GAP : 0)
        ), 0)
        let childLeft = centerX - childrenSpan / 2
        children.forEach((childID) => {
            placeSubtree(childID, childLeft)
            childLeft += (spanByID.get(childID) || NODE_WIDTH) + HORIZONTAL_GAP
        })
    }

    placeSubtree(rootNode.id, contentLeft)
    const positionedNodes = nodes.map((node) => positionedByID.get(node.id) || node)
    const height = Math.max(
        MIN_GRAPH_HEIGHT,
        ...positionedNodes.map((node) => node.y + node.height + GRAPH_PADDING_Y),
    )
    const positionedRoot = positionedByID.get(rootNode.id) || rootNode

    return {
        nodes: positionedNodes,
        edges,
        width,
        height,
        center: {
            x: positionedRoot.x + positionedRoot.width / 2,
            y: positionedRoot.y + positionedRoot.height / 2,
        },
    }
}

export const buildListenerJourneyGraph = (data: IListenerJourney): ListenerJourneyGraph => {
    const nodes = new Map<string, ListenerJourneyGraphNode>()
    const edges = new Map<string, ListenerJourneyGraphEdge>()
    const fullName = getFullName(data)
    const fullNameFrom = asText(data.listener.fullNameFrom) || fullName

    const addNode = (node: Omit<ListenerJourneyGraphNode, 'x' | 'y' | 'width' | 'height'>, listener = false) => {
        if (nodes.has(node.id)) return
        nodes.set(node.id, {
            ...node,
            x: 0,
            y: 0,
            width: listener ? LISTENER_WIDTH : NODE_WIDTH,
            height: listener ? LISTENER_HEIGHT : NODE_HEIGHT,
        })
    }
    const addEdge = (from: string, to: string, kind: 'process' | 'relation', state: ListenerJourneyNodeState) => {
        if (!nodes.has(from) || !nodes.has(to)) return
        const id = `${kind}-${from}-${to}`
        if (!edges.has(id)) edges.set(id, {id, from, to, kind, state})
    }

    const listenerNodeID = `listener-${data.listener.listenerID}`
    addNode({
        id: listenerNodeID,
        kind: 'listener',
        stage: 'Слушатель',
        title: fullName || `Слушатель №${data.listener.listenerID}`,
        subtitle: asText(data.listener.phoneNumber) || 'Телефон не указан',
        detail: asText(data.listener.snils) ? `СНИЛС ${data.listener.snils}` : `Анкета №${data.listener.listenerID}`,
        state: 'active',
        action: {type: 'listener', cardID: data.listener.listenerID},
    }, true)

    const uniqueContracts = [...new Map(data.contracts.map((item) => [item.contractID, item])).values()]
    const uniqueApplications = [...new Map(data.applications.map((item) => [item.applicationID, item])).values()]
    const applicationIDs = [...new Set([
        ...uniqueApplications.map((item) => Number(item.applicationID)),
        ...data.commercialOffers.map((item) => Number(item.applicationID)),
        ...uniqueContracts.map((item) => Number(item.applicationID)),
    ].filter((applicationID) => applicationID > 0))]

    applicationIDs.forEach((applicationID) => {
        const application = uniqueApplications.find((item) => item.applicationID === applicationID)
        addNode({
            id: `application-${applicationID}`,
            kind: 'application',
            stage: 'Заявка',
            title: `Заявка №${applicationID}`,
            subtitle: application ? asText(application.statusName) || 'Статус не указан' : 'Данные заявки не получены',
            detail: application ? asText(application.dateCreate) : undefined,
            state: application ? 'active' : 'pending',
            action: {type: 'application', cardID: applicationID},
        })
    })

    data.commercialOffers.forEach((offer) => addNode({
        id: `commercial-offer-${offer.commercialOfferID}`,
        kind: 'commercialOffer',
        stage: 'Коммерческое предложение',
        title: `КП №${offer.commercialOfferID}`,
        subtitle: asText(offer.statusName) || 'Статус не указан',
        detail: asText(offer.commercialOfferDate),
        state: 'active',
        action: {type: 'commercialOffer', cardID: offer.commercialOfferID},
    }))

    uniqueContracts.forEach((contract) => {
        addNode({
            id: `contract-${contract.contractID}`,
            kind: 'contract',
            stage: 'Договор',
            title: asText(contract.contractNumber) ? `Договор ${contract.contractNumber}` : `Договор №${contract.contractID}`,
            subtitle: asText(contract.contractStatusName) || 'Статус не указан',
            detail: asText(contract.contractTypeName),
            state: getContractState(contract),
            action: {type: 'contract', cardID: contract.contractID},
        })

        if (Number(contract.companyID) > 0) {
            const company = data.companies.find((item) => item.companyID === Number(contract.companyID))
            addNode({
                id: `company-${contract.companyID}`,
                kind: 'company',
                stage: 'Юридическое лицо',
                title: asText(company?.companyName) || asText(contract.companyName) || `Контрагент №${contract.companyID}`,
                subtitle: asText(company?.companyINN || contract.companyINN) ? `ИНН ${company?.companyINN || contract.companyINN}` : 'ИНН не указан',
                state: 'active',
                action: {type: 'company', cardID: Number(contract.companyID)},
            })
        }

        const signature = data.signatures.find((item) => item.contractID === contract.contractID)
        addNode({
            id: `signature-${contract.contractID}`,
            kind: 'signature',
            stage: 'Подписание',
            title: 'Подписание договора',
            subtitle: signature ? asText(signature.statusName) || 'Анкета создана' : 'Анкета ещё не создана',
            detail: asText(contract.contractNumber),
            state: signature ? 'active' : 'pending',
            action: signature ? {type: 'signature', cardID: contract.contractID, contractNumber: contract.contractNumber} : undefined,
        })

        addNode({
            id: `delivery-${contract.contractID}`,
            kind: 'delivery',
            stage: 'Доставка',
            title: contract.isDelivery ? 'Документ доставлен' : 'Доставка документа',
            subtitle: contract.isDelivery
                ? 'Цикл завершён'
                : asText(contract.deliveryAddress) || 'Этап ещё не завершён',
            detail: asText(contract.contractNumber),
            state: contract.isDelivery ? 'complete' : contract.isScan || asText(contract.deliveryAddress) ? 'active' : 'pending',
            action: contract.contractStatusID === 4 || contract.contractStatusID === 5 || contract.isScan || contract.isDelivery
                ? {type: 'delivery', cardID: contract.contractID}
                : undefined,
        })
    })

    const branches = [...new Map(data.contracts.map((contract) => [
        branchKey(contract.contractID, contract.contractProgramID),
        contract,
    ])).values()]

    branches.forEach((contract) => {
        const key = branchKey(contract.contractID, contract.contractProgramID)
        const finalExamination = data.finalExaminations.find((item) =>
            item.contractID === contract.contractID && item.contractProgramID === contract.contractProgramID)
        const printing = data.printings.find((item) =>
            item.contractID === contract.contractID && item.contractProgramID === contract.contractProgramID)
        const finalNodeID = finalExamination ? `final-examination-${finalExamination.finalExaminationID}` : `final-examination-pending-${key}`
        const printingNodeID = printing ? `printing-${printing.printID}` : `printing-pending-${key}`
        const scanNodeID = printing ? `scan-${printing.printID}` : `scan-pending-${key}`

        addNode({
            id: finalNodeID,
            kind: 'finalExamination',
            stage: 'Итоговая аттестация',
            title: asText(contract.courseName) || 'Итоговая аттестация',
            subtitle: finalExamination
                ? finalExamination.toPrint ? 'Допущен к печати' : 'Анкета создана'
                : 'Этап ещё не создан',
            detail: asText(contract.attestationTypeName),
            state: finalExamination ? getFinalExaminationState(finalExamination) : 'pending',
            action: finalExamination ? {
                type: 'finalExamination',
                cardID: finalExamination.finalExaminationID,
                fullNameFrom,
                attestationType: finalExamination.attestationTypeName || contract.attestationTypeName,
                dataBase: contract.dataBase,
            } : undefined,
        })
        addNode({
            id: printingNodeID,
            kind: 'printing',
            stage: 'Печать документа',
            title: printing?.diplomNumber ? `${contract.documentTypeName || 'Документ'} №${printing.diplomNumber}` : contract.documentTypeName || 'Печать документа',
            subtitle: printing ? asText(printing.printStatusName) || 'Анкета печати создана' : 'Ожидает итоговую аттестацию',
            detail: asText(contract.courseName),
            state: printing ? getPrintingState(printing) : 'pending',
            action: printing ? {type: 'printing', cardID: printing.printID, fullNameFrom, dataBase: printing.dataBase || contract.dataBase} : undefined,
        })
        addNode({
            id: scanNodeID,
            kind: 'scan',
            stage: 'Скан документа',
            title: printing?.scan ? 'Скан загружен' : 'Скан документа',
            subtitle: printing?.scan ? printing.scan.fileName : 'Файл ещё не загружен',
            detail: printing?.scan?.fileSize,
            state: printing?.scan ? 'complete' : 'pending',
            action: printing?.scan ? {type: 'scan', fileUrl: printing.scan.filePath, fileName: printing.scan.fileName} : undefined,
        })

        addEdge(`signature-${contract.contractID}`, finalNodeID, 'process', finalExamination ? 'active' : 'pending')
        addEdge(finalNodeID, printingNodeID, 'process', printing ? 'active' : 'pending')
        addEdge(printingNodeID, scanNodeID, 'process', printing?.scan ? 'complete' : 'pending')
        addEdge(scanNodeID, `delivery-${contract.contractID}`, 'process', contract.isDelivery ? 'complete' : 'pending')
    })

    const connectContract = (contract: IListenerJourneyContract) => {
        const contractNodeID = `contract-${contract.contractID}`
        if (Number(contract.companyID) > 0) addEdge(contractNodeID, `company-${contract.companyID}`, 'relation', 'active')
        addEdge(contractNodeID, `signature-${contract.contractID}`, 'process', data.signatures.some((item) => item.contractID === contract.contractID) ? 'active' : 'pending')
    }

    const addPendingContractPath = (parentNodeIDs: string[], branchID = '') => {
        const suffix = branchID ? `-${branchID}` : ''
        const pendingStages: Array<{id: string; kind: Exclude<ListenerJourneyNodeKind, 'listener' | 'application' | 'commercialOffer'>; stage: string}> = [
            {id: `contract-pending${suffix}`, kind: 'contract', stage: 'Договор'},
            {id: `signature-pending${suffix}`, kind: 'signature', stage: 'Подписание'},
            {id: `final-examination-pending${suffix}`, kind: 'finalExamination', stage: 'Итоговая аттестация'},
            {id: `printing-pending${suffix}`, kind: 'printing', stage: 'Печать документа'},
            {id: `scan-pending${suffix}`, kind: 'scan', stage: 'Скан документа'},
            {id: `delivery-pending${suffix}`, kind: 'delivery', stage: 'Доставка'},
        ]
        pendingStages.forEach((item) => addNode({
            ...item,
            title: item.stage,
            subtitle: 'Этап ещё не создан',
            state: 'pending',
        }))
        parentNodeIDs.forEach((parentNodeID) => addEdge(parentNodeID, pendingStages[0].id, 'process', 'pending'))
        pendingStages.slice(1).forEach((item, index) => addEdge(pendingStages[index].id, item.id, 'process', 'pending'))
    }

    applicationIDs.forEach((applicationID) => {
        const applicationNodeID = `application-${applicationID}`
        const application = uniqueApplications.find((item) => item.applicationID === applicationID)
        const offers = data.commercialOffers.filter((item) => item.applicationID === applicationID)
        const contracts = uniqueContracts.filter((item) => Number(item.applicationID) === applicationID)
        const offerNodeIDs = offers.length > 0
            ? offers.map((item) => `commercial-offer-${item.commercialOfferID}`)
            : [`commercial-offer-pending-${applicationID}`]

        if (offers.length === 0) addNode({
            id: offerNodeIDs[0],
            kind: 'commercialOffer',
            stage: 'Коммерческое предложение',
            title: 'Коммерческое предложение',
            subtitle: 'Анкета ещё не создана',
            state: 'pending',
        })

        addEdge(listenerNodeID, applicationNodeID, 'process', application ? 'active' : 'pending')
        offerNodeIDs.forEach((offerNodeID) => addEdge(applicationNodeID, offerNodeID, 'process', offers.length > 0 ? 'active' : 'pending'))

        if (contracts.length === 0) {
            addPendingContractPath(offerNodeIDs, `application-${applicationID}`)
            return
        }

        contracts.forEach((contract, contractIndex) => {
            const contractNodeID = `contract-${contract.contractID}`
            const primaryOfferNodeID = offerNodeIDs[contractIndex % offerNodeIDs.length]
            addEdge(primaryOfferNodeID, contractNodeID, 'process', offers.length > 0 ? getContractState(contract) : 'pending')
            offerNodeIDs
                .filter((offerNodeID) => offerNodeID !== primaryOfferNodeID)
                .forEach((offerNodeID) => addEdge(offerNodeID, contractNodeID, 'relation', getContractState(contract)))
            connectContract(contract)
        })
    })

    uniqueContracts
        .filter((contract) => Number(contract.applicationID) <= 0)
        .forEach((contract) => {
            const applicationNodeID = `application-pending-${contract.contractID}`
            const offerNodeID = `commercial-offer-pending-contract-${contract.contractID}`
            addNode({
                id: applicationNodeID,
                kind: 'application',
                stage: 'Заявка',
                title: 'Заявка не связана',
                subtitle: 'Номер заявки отсутствует',
                state: 'pending',
            })
            addNode({
                id: offerNodeID,
                kind: 'commercialOffer',
                stage: 'Коммерческое предложение',
                title: 'Коммерческое предложение',
                subtitle: 'Анкета ещё не создана',
                state: 'pending',
            })
            addEdge(listenerNodeID, applicationNodeID, 'process', 'pending')
            addEdge(applicationNodeID, offerNodeID, 'process', 'pending')
            addEdge(offerNodeID, `contract-${contract.contractID}`, 'process', 'pending')
            connectContract(contract)
        })

    if (applicationIDs.length === 0 && uniqueContracts.length === 0) {
        addNode({
            id: 'application-pending',
            kind: 'application',
            stage: 'Заявка',
            title: 'Заявка',
            subtitle: 'Этап ещё не создан',
            state: 'pending',
        })
        addNode({
            id: 'commercial-offer-pending',
            kind: 'commercialOffer',
            stage: 'Коммерческое предложение',
            title: 'Коммерческое предложение',
            subtitle: 'Этап ещё не создан',
            state: 'pending',
        })
        addEdge(listenerNodeID, 'application-pending', 'process', 'pending')
        addEdge('application-pending', 'commercial-offer-pending', 'process', 'pending')
        addPendingContractPath(['commercial-offer-pending'])
    }

    return positionNodes(Array.from(nodes.values()), Array.from(edges.values()))
}